import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import SEO from "@/components/SEO";
import InstructorRoutingPanel from "@/components/admin/InstructorRoutingPanel";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Activity,
  AlertTriangle,
  Ban,
  CheckCircle2,
  Image as ImageIcon,
  Loader2,
  RefreshCw,
  ShieldAlert,
  Video,
} from "lucide-react";

/**
 * /admin/content-generation — operator console for the HeirWise Content
 * Generation Engine. Every read and action goes through the admin-authenticated
 * `generation-orchestrator` function; nothing here is learner-facing.
 */

type Row = Record<string, unknown>;

interface Overview {
  requests: Row[];
  jobs: Row[];
  content_gaps: Row[];
  kill_switches: Row[];
  providers: Row[];
  presenters: Row[];
  credentials: Record<string, boolean>;
  intervention_versions: Row[];
}

async function callOrchestrator<T>(body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke("generation-orchestrator", { body });
  if (error) throw error;
  if (data?.error) throw new Error(String(data.error));
  return data as T;
}

const LIFECYCLE_TONE: Record<string, string> = {
  ACTIVE: "bg-emerald-500/10 text-emerald-600 border-emerald-500/30",
  CALIBRATING: "bg-amber-500/10 text-amber-600 border-amber-500/30",
  QUARANTINED: "bg-destructive/10 text-destructive border-destructive/30",
  FLAGGED: "bg-destructive/10 text-destructive border-destructive/30",
};

function StatusBadge({ value }: { value: string }) {
  return (
    <Badge variant="outline" className={LIFECYCLE_TONE[value] ?? "bg-muted text-muted-foreground"}>
      {value}
    </Badge>
  );
}

function Section({ title, icon, children }: { title: string; icon?: React.ReactNode; children: React.ReactNode }) {
  return (
    <section className="rounded-2xl border border-border bg-card p-5 sm:p-6 space-y-4">
      <h2 className="font-heading text-lg font-semibold flex items-center gap-2">
        {icon}
        {title}
      </h2>
      {children}
    </section>
  );
}

export default function AdminContentGeneration() {
  const qc = useQueryClient();
  const [auditVersionId, setAuditVersionId] = useState<string | null>(null);
  const [runLessonId, setRunLessonId] = useState("");
  const [runObjective, setRunObjective] = useState("");
  const [liveRun, setLiveRun] = useState(false);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["cg-overview"],
    queryFn: () => callOrchestrator<Overview>({ action: "overview" }),
    staleTime: 15_000,
  });

  const { data: audit, isFetching: auditLoading } = useQuery({
    queryKey: ["cg-audit", auditVersionId],
    enabled: Boolean(auditVersionId),
    queryFn: () => callOrchestrator<Row>({ action: "audit_chain", intervention_version_id: auditVersionId }),
  });

  const act = useMutation({
    mutationFn: (body: Record<string, unknown>) => callOrchestrator<Row>(body),
    onSuccess: (result) => {
      toast.success(`Done — ${JSON.stringify(result).slice(0, 140)}`);
      qc.invalidateQueries({ queryKey: ["cg-overview"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const overview = data;

  return (
    <div className="min-h-screen bg-background p-6 sm:p-10">
      <SEO
        title="Content Generation Engine — Admin"
        description="Operator console for grounded infographic and video generation."
        path="/admin/content-generation"
      />
      <div className="max-w-6xl mx-auto space-y-8">
        <header className="space-y-2">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground">
            <Activity className="h-3.5 w-3.5" /> Ops
          </div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold">Content Generation Engine</h1>
          <p className="text-sm text-muted-foreground">
            Registry-first, source-grounded, independently validated. Nothing generated here reaches a learner
            until it passes validation and is explicitly activated.
          </p>
        </header>

        {isLoading && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading engine state…
          </div>
        )}
        {isError && (
          <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <div>{(error as Error).message}</div>
          </div>
        )}

        {overview && (
          <>
            <Section title="Providers & kill switches" icon={<ShieldAlert className="h-4 w-4" />}>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-2">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Providers</p>
                  {overview.providers.map((p) => (
                    <div key={String(p.provider_key)} className="flex items-center justify-between gap-3 rounded-lg border border-border px-3 py-2">
                      <div className="text-sm">
                        <div className="font-medium">{String(p.display_name ?? p.provider_key)}</div>
                        <div className="text-xs text-muted-foreground">{String(p.model_id ?? "—")}</div>
                        <div className="mt-1">
                          <Badge
                            variant="outline"
                            className={p.credential_configured
                              ? "bg-emerald-500/10 text-emerald-600 border-emerald-500/30"
                              : "bg-amber-500/10 text-amber-600 border-amber-500/30"}
                          >
                            {p.credential_configured ? "Credential configured" : "Credential missing"}
                          </Badge>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant={p.enabled ? "secondary" : "outline"}
                        disabled={!p.credential_configured && !p.enabled}
                        onClick={() =>
                          act.mutate({ action: "set_provider_enabled", provider_key: p.provider_key, enabled: !p.enabled })}
                      >
                        {p.enabled ? "Enabled" : "Disabled"}
                      </Button>
                    </div>
                  ))}
                </div>
                <div className="space-y-2">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Kill switches</p>
                  {overview.kill_switches.map((k) => (
                    <div key={String(k.switch_key)} className="flex items-center justify-between gap-3 rounded-lg border border-border px-3 py-2">
                      <div className="text-sm font-medium">{String(k.switch_key)}</div>
                      <Button
                        size="sm"
                        variant={k.enabled ? "secondary" : "outline"}
                        onClick={() => act.mutate({ action: "set_kill_switch", switch_key: k.switch_key, enabled: !k.enabled })}
                      >
                        {k.enabled ? "Generation allowed" : "Halted"}
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            </Section>

            <InstructorRoutingPanel />

            <Section title="Presenters" icon={<Video className="h-4 w-4" />}>
              <p className="text-sm text-muted-foreground">
                A live talking-head render needs the presenter's canonical still image. Paths are private
                ("bucket/path"); the renderer only ever receives a short-lived signed link.
              </p>
              <div className="space-y-2">
                {overview.presenters.map((pr) => (
                  <div key={String(pr.presenter_id)} className="flex flex-wrap items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm">
                    <div className="min-w-40 font-medium">
                      {String(pr.display_name)}
                      {pr.is_default ? <span className="ml-2 text-xs text-muted-foreground">default</span> : null}
                    </div>
                    <input
                      className="flex-1 min-w-56 rounded-md border border-border bg-background px-2 py-1 text-xs"
                      placeholder="avatars/aelyra/portrait.png"
                      defaultValue={String(pr.canonical_image_path ?? "")}
                      onBlur={(e) => {
                        const next = e.target.value.trim();
                        if (next && next !== String(pr.canonical_image_path ?? "")) {
                          act.mutate({
                            action: "set_presenter_image",
                            presenter_id: pr.presenter_id,
                            canonical_image_path: next,
                          });
                        }
                      }}
                    />
                    <span className="text-xs text-muted-foreground">{String(pr.voice_id)}</span>
                  </div>
                ))}
              </div>
            </Section>


            <Section title="Generation requests" icon={<RefreshCw className="h-4 w-4" />}>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <tr>
                      <th className="py-2 pr-3">Modality</th>
                      <th className="py-2 pr-3">Competency</th>
                      <th className="py-2 pr-3">Status</th>
                      <th className="py-2 pr-3">Reason</th>
                      <th className="py-2 pr-3">Mode</th>
                    </tr>
                  </thead>
                  <tbody>
                    {overview.requests.slice(0, 20).map((r) => (
                      <tr key={String(r.request_id)} className="border-t border-border/60">
                        <td className="py-2 pr-3 capitalize">{String(r.requested_modality)}</td>
                        <td className="py-2 pr-3">{String(r.competency_key ?? "—")}</td>
                        <td className="py-2 pr-3"><StatusBadge value={String(r.status)} /></td>
                        <td className="py-2 pr-3 max-w-sm truncate text-muted-foreground">{String(r.status_reason ?? "")}</td>
                        <td className="py-2 pr-3 text-xs text-muted-foreground">{r.is_dry_run ? "dry run" : "live"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Section>

            <Section title="Jobs" icon={<Loader2 className="h-4 w-4" />}>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <tr>
                      <th className="py-2 pr-3">Modality</th>
                      <th className="py-2 pr-3">Stage</th>
                      <th className="py-2 pr-3">Status</th>
                      <th className="py-2 pr-3">Provider</th>
                      <th className="py-2 pr-3">Attempts</th>
                      <th className="py-2 pr-3">Error</th>
                      <th className="py-2 pr-3" />
                    </tr>
                  </thead>
                  <tbody>
                    {overview.jobs.slice(0, 20).map((j) => (
                      <tr key={String(j.job_id)} className="border-t border-border/60">
                        <td className="py-2 pr-3 capitalize">{String(j.modality)}</td>
                        <td className="py-2 pr-3">{String(j.pipeline_stage ?? "—")}</td>
                        <td className="py-2 pr-3"><StatusBadge value={String(j.status)} /></td>
                        <td className="py-2 pr-3 text-xs">{String(j.provider)} · {String(j.provider_model ?? "")}</td>
                        <td className="py-2 pr-3">{String(j.attempts)}/{String(j.max_attempts)}</td>
                        <td className="py-2 pr-3 max-w-xs truncate text-destructive">{String(j.error_message ?? "")}</td>
                        <td className="py-2 pr-3">
                          <Button size="sm" variant="outline" onClick={() => act.mutate({ action: "retry_job", job_id: j.job_id })}>
                            Retry
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Section>

            <Section title="Intervention versions" icon={<CheckCircle2 className="h-4 w-4" />}>
              <div className="space-y-2">
                {overview.intervention_versions.slice(0, 20).map((v) => (
                  <div key={String(v.intervention_version_id)} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border px-3 py-2 text-sm">
                    <div className="flex items-center gap-2">
                      <StatusBadge value={String(v.lifecycle_status)} />
                      <span className="text-xs text-muted-foreground">
                        {String(v.provider ?? "—")} · {v.is_dry_run ? "dry run" : "live"}
                      </span>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => setAuditVersionId(String(v.intervention_version_id))}>
                        Audit chain
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => act.mutate({
                          action: "set_lifecycle",
                          intervention_version_id: v.intervention_version_id,
                          lifecycle_status: "ACTIVE",
                        })}
                      >
                        Activate
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => act.mutate({
                          action: "set_lifecycle",
                          intervention_version_id: v.intervention_version_id,
                          lifecycle_status: "QUARANTINED",
                          reason: "Admin quarantine",
                        })}
                      >
                        <Ban className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </Section>

            <Section title="Content gaps" icon={<AlertTriangle className="h-4 w-4" />}>
              {overview.content_gaps.length === 0
                ? <p className="text-sm text-muted-foreground">No open content gaps.</p>
                : (
                  <ul className="space-y-2 text-sm">
                    {overview.content_gaps.map((g) => (
                      <li key={String(g.request_id)} className="rounded-lg border border-border px-3 py-2">
                        <div className="font-medium">{String(g.competency_key ?? g.lesson_id ?? "—")}</div>
                        <div className="text-xs text-muted-foreground">{String(g.status_reason ?? "")}</div>
                      </li>
                    ))}
                  </ul>
                )}
            </Section>

            <Section title="Controlled generation" icon={<ImageIcon className="h-4 w-4" />}>
              <p className="text-sm text-muted-foreground">
                Runs the full grounded pipeline. A live run calls the real providers and is billed; it obeys the
                provider switches above, and a disabled or unconfigured provider returns a configuration error
                instead of guessing. Output starts in calibration and reaches no learner until it is activated.
              </p>
              <div className="grid gap-2 sm:grid-cols-2">
                <input
                  className="rounded-md border border-border bg-background px-3 py-2 text-sm"
                  placeholder="Lesson ID"
                  value={runLessonId}
                  onChange={(e) => setRunLessonId(e.target.value)}
                />
                <input
                  className="rounded-md border border-border bg-background px-3 py-2 text-sm"
                  placeholder="Instructional objective"
                  value={runObjective}
                  onChange={(e) => setRunObjective(e.target.value)}
                />
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={liveRun} onChange={(e) => setLiveRun(e.target.checked)} />
                Live run (real providers, billed). Unchecked = stubbed dry run.
              </label>
              <div className="flex flex-wrap gap-2">
                <Button
                  disabled={act.isPending || !runLessonId.trim() || !runObjective.trim()}
                  onClick={() => act.mutate({
                    action: "resolve_and_run",
                    request: {
                      modality: "video",
                      lesson_id: runLessonId.trim(),
                      instructional_objective: runObjective.trim(),
                      is_dry_run: !liveRun,
                      idempotency_key: `admin_video:${runLessonId.trim()}:${liveRun ? "live" : "dry"}:${Date.now()}`,
                    },
                  })}
                >
                  <Video className="h-4 w-4 mr-1" /> {liveRun ? "Generate video (live)" : "Generate video (dry run)"}
                </Button>
                <Button
                  variant="secondary"
                  disabled={act.isPending}
                  onClick={() => act.mutate({ action: "run_job" })}
                >
                  <RefreshCw className="h-4 w-4 mr-1" /> Run next queued job
                </Button>
                <Button
                  variant="outline"
                  disabled={act.isPending}
                  onClick={() => act.mutate({ action: "set_kill_switch", switch_key: "generation_global", enabled: false, reason: "Admin halt" })}
                >
                  <Ban className="h-4 w-4 mr-1" /> Halt all generation
                </Button>
              </div>
            </Section>


            {auditVersionId && (
              <Section title="Audit chain" icon={<Video className="h-4 w-4" />}>
                {auditLoading
                  ? <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
                  : (
                    <pre className="max-h-[32rem] overflow-auto rounded-lg bg-muted p-4 text-xs">
                      {JSON.stringify(audit, null, 2)}
                    </pre>
                  )}
              </Section>
            )}
          </>
        )}
      </div>
    </div>
  );
}
